import { useMeetingRecap } from './useMeetingRecap';

import {
    useGetMeetingTranscript,
    useUploadMeetingTranscript,
} from '@/api/review-services/post-review-meeting';
import postReviewMeetingService from '@/api/review-services/post-review-meeting/postReviewMeetingService';
import { MeetingTranscript, useMeetingTranscriptScreen } from '@/components/MeetingTranscript';
import { useAmelioTranslations } from '@/hooks';
import { showErrorToast } from '@/plugins/Toast';
import { downloadFile } from '@/utils/fileDownload';

/** Mirrors `ManagerAssessmentController`'s own limit for this endpoint. */
const MAX_FILE_SIZE_MB = 10;

interface MeetingTranscriptUploadProps {
    cycleInstanceId: string;
    dataTestId?: string;
    disabled?: boolean;
}

/**
 * Evaluation-journey binding of the shared transcript screen: the transcript of a cycle instance, the
 * recap generation around it, and the `postReviewMeeting.transcript.*` wording.
 *
 * It is the only journey that hands the transcript itself back, beside the recap.
 */
export const MeetingTranscriptUpload = ({
    cycleInstanceId,
    dataTestId = 'meeting-transcript-upload',
    disabled = false,
}: MeetingTranscriptUploadProps) => {
    const { t } = useAmelioTranslations();

    const transcriptQuery = useGetMeetingTranscript(cycleInstanceId);
    const upload = useUploadMeetingTranscript(cycleInstanceId);
    const recapGeneration = useMeetingRecap(
        cycleInstanceId,
        transcriptQuery.data?.pendingRecapCorrelationId,
    );
    const screen = useMeetingTranscriptScreen({
        namespace: 'postReviewMeeting.transcript',
        transcriptQuery,
        upload,
        recapGeneration,
        maxFileSizeMb: MAX_FILE_SIZE_MB,
        disabled,
    });

    const fileName = transcriptQuery.data?.fileName;

    // The transcript itself, fetched only now. It is the most sensitive thing the module holds, so it
    // never rides along with the page load: it is asked for at the click, handed over, and nothing of
    // it is kept.
    const handleDownloadTranscript = async () => {
        try {
            const file = await postReviewMeetingService.downloadMeetingTranscript(cycleInstanceId);
            downloadFile(file, fileName ?? 'meeting-transcript.txt');
        } catch {
            showErrorToast({ message: t('postReviewMeeting.transcript.downloadError') });
        }
    };

    return (
        <MeetingTranscript
            {...screen}
            labels={{
                ...screen.labels,
                downloadTranscript: t('postReviewMeeting.transcript.downloadTranscript'),
            }}
            // Offered only once there is something to hand back: a transcript exists before its
            // recap does, so the two appear at different moments.
            onDownloadTranscript={fileName ? () => void handleDownloadTranscript() : undefined}
            dataTestId={dataTestId}
        />
    );
};

export default MeetingTranscriptUpload;
